import React, { useState, useCallback, useEffect, useRef } from 'react';
import { registerBlockType } from '@quillforms/blocks';
import DualFileInputContainer from '../ui/DualFileInputContainer';
import { serializeFile } from '@/utils/fileUtils';
import {
  trackFileSelected,
  trackFileUploadSuccess,
  trackFileUploadError,
  trackFileRemoved,
} from '@/utils/rum-tracking';

// Register the custom file-upload block following QuillForms documentation pattern
registerBlockType("custom-file-upload", {
  supports: {
    editable: true, // If the block has an answer or not.
  },
  // The custom attributes for the block.
  attributes: {
    label: {
      type: "string",
      default: "Upload Files"
    },
    description: {
      type: "string",
      default: "Please upload both sides of your card"
    },
    
    required: {
      type: "boolean",
      default: false
    },
    allowedFileTypes: {
      type: "array",
      default: ["image/jpeg", "image/png", "image/jpg", "application/pdf"]
    },
    maxFileSize: {
      type: "number",
      default: 5242880 // 5MB
    },
    frontDescription: {
      type: "string",
      default: "Upload a clear image of the front side"
    },
    backDescription: {
      type: "string",
      default: "Upload a clear image of the back side"
    }
  },

  // Block display, this should be a react component following the QuillForms pattern
  display: ({ id, attributes, setIsValid, setIsAnswered, setValidationErr, showNextBtn, blockWithError, val, setVal, showErrMsg }: any) => {
    const {
      label = "Upload Files",
      description = "Please upload both sides of your card",
      required = false,
      allowedFileTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"],
      frontDescription = "Upload a clear image of the front side",
      backDescription = "Upload a clear image of the back side"
    } = attributes;

    const [frontFile, setFrontFile] = useState<File | null>(null);
    const [backFile, setBackFile] = useState<File | null>(null);
    // The change handlers and handleValidationComplete fire in the same tick, so
    // errors are read from a ref; the run id drops serializations that finished
    // after a newer validation started.
    const sideErrors = useRef<{ front: string | null; back: string | null }>({ front: null, back: null });
    const validationRunId = useRef(0);

    // Handle front file changes
    const handleFrontFileChange = useCallback((file: File | null, error: string | null) => {
      setFrontFile(file);
      sideErrors.current.front = error;

      if (error) {
        trackFileUploadError(id, 'front', error);
        blockWithError(`Front file: ${error}`);
        setIsValid(false);
        setValidationErr(`Front file: ${error}`);
        showErrMsg(true);
      } else if (file) {
        trackFileSelected(id, 'front', file.type, file.size);
        showErrMsg(false);
      } else {
        // file set to null = removed
        trackFileRemoved(id, 'front');
        showErrMsg(false);
      }
    }, [id, blockWithError, setIsValid, setValidationErr, showErrMsg]);

    // Handle back file changes
    const handleBackFileChange = useCallback((file: File | null, error: string | null) => {
      setBackFile(file);
      sideErrors.current.back = error;

      if (error) {
        trackFileUploadError(id, 'back', error);
        blockWithError(`Back file: ${error}`);
        setIsValid(false);
        setValidationErr(`Back file: ${error}`);
        showErrMsg(true);
      } else if (file) {
        trackFileSelected(id, 'back', file.type, file.size);
        showErrMsg(false);
      } else {
        // file set to null = removed
        trackFileRemoved(id, 'back');
        showErrMsg(false);
      }
    }, [id, blockWithError, setIsValid, setValidationErr, showErrMsg]);

    // Helper function to serialize a file with error handling
    const serializeFileWithErrorHandling = useCallback(async (file: File | null): Promise<string | null> => {
      if (!file) return null;
      
      try {
        return await serializeFile(file);
      } catch (error) {
        console.error(`Error serializing file: ${error instanceof Error ? error.message : String(error)}`);
        blockWithError(`Error processing file: ${file.name}`);
        setIsValid(false);
        setValidationErr(`Error processing file: ${file.name}`);
        showErrMsg(true);
        return null;
      }
    }, [blockWithError, setIsValid, setValidationErr, showErrMsg]);

    // Handle overall validation completion
    const handleValidationComplete = useCallback(async (frontValid: boolean, backValid: boolean, frontFileResult: File | null, backFileResult: File | null) => {
      const bothFilesUploaded = frontValid && backValid;
      const anyFileUploaded = frontValid || backValid;
      const hasErrors = Boolean(sideErrors.current.front || sideErrors.current.back);
      const runId = ++validationRunId.current;
      const isStale = () => validationRunId.current !== runId;
      
      // Update QuillForms state based on files
      if (bothFilesUploaded && !hasErrors) {
        try {
          const serializedFrontFile = await serializeFileWithErrorHandling(frontFileResult);
          const serializedBackFile = await serializeFileWithErrorHandling(backFileResult);
          if (isStale()) return;

          if (serializedFrontFile && serializedBackFile) {
            trackFileUploadSuccess(id, 'both');
            setVal({
              front: serializedFrontFile,
              back: serializedBackFile
            });
            setIsAnswered(true);
            setIsValid(true);
            setValidationErr(null);
            showNextBtn(true);
            showErrMsg(false);
          }
        } catch (error) {
          if (isStale()) return;
          console.error(`Error in file serialization: ${error instanceof Error ? error.message : String(error)}`);
          blockWithError("Error processing files");
          setIsValid(false);
          setValidationErr("Error processing files");
          showErrMsg(true);
        }
      } else if (anyFileUploaded && (!hasErrors || !required)) {
        // At least one file uploaded, check if required
        if (required) {
          // Both files required
          setIsValid(false);
          setValidationErr("Both front and back files are required");
          showNextBtn(false);
        } else {
          try {
            // Serialize available files
            const serializedFrontFile = frontFileResult ? await serializeFileWithErrorHandling(frontFileResult) : null;
            const serializedBackFile = backFileResult ? await serializeFileWithErrorHandling(backFileResult) : null;
            if (isStale()) return;
            
            // Partial upload is acceptable
            setVal({
              front: serializedFrontFile,
              back: serializedBackFile
            });
            setIsAnswered(true);
            setIsValid(true);
            setValidationErr(null);
            showNextBtn(true);
            showErrMsg(false);
          } catch (error) {
            if (isStale()) return;
            console.error(`Error in file serialization: ${error instanceof Error ? error.message : String(error)}`);
            blockWithError("Error processing files");
            setIsValid(false);
            setValidationErr("Error processing files");
            showErrMsg(true);
          }
        }
      } else if (!anyFileUploaded) {
        // No files uploaded
        setVal(null);
        setIsAnswered(false);
        if (required) {
          setIsValid(false);
          setValidationErr("File upload is required");
          showNextBtn(false);
        } else {
          setIsValid(true);
          setValidationErr(null);
          showNextBtn(true);
        }
      }
    }, [required, setVal, setIsAnswered, setIsValid, setValidationErr, showNextBtn, showErrMsg, blockWithError, serializeFileWithErrorHandling]);

    // Initialize validation state
    useEffect(() => {
      if (required && !frontFile && !backFile) {
        setIsValid(false);
        setValidationErr("File upload is required");
      } else {
        setIsValid(true);
        setValidationErr(null);
      }
    }, [required, frontFile, backFile, setIsValid, setValidationErr]);

    // Convert allowedFileTypes array to accept string
    const acceptString = allowedFileTypes.join(',');

    return (
      <div className="w-full max-w-4xl mx-auto space-y-6">
        {/* Header */}
        {/* <div className="space-y-2 text-center">
          <h2 className="text-2xl font-bold text-foreground">{label}</h2>
          {description && (
            <p className="text-muted-foreground">{description}</p>
          )}
        </div> */}

        {/* Dual File Input Container */}
        <DualFileInputContainer
          onFrontFileChange={handleFrontFileChange}
          onBackFileChange={handleBackFileChange}
          onValidationComplete={handleValidationComplete}
          accept={acceptString}
          frontDescription={frontDescription}
          backDescription={backDescription}
          className="w-full"
        />
      </div>
    );
  }
});

export default registerBlockType;